import React, { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { Feather } from '@expo/vector-icons';

import {
	CLIENT_LEAD_STATUS,
	CLIENT_LEAD_STATUS_NAMES,
	CLIENT_LEAD_TYPE,
} from '../../../common/constants';
import { getUserName } from '../../../common/utils/user/getUserName';
import { useUserContext } from '../../../common/utils/context';
import { GET_MOBILE_LEADS } from '../../graphql/lead';
import { List } from '../../components';
import { ListContainer } from '../../containers';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { EmptyState, ListRow, Screen, SecondaryButton, Tag } from '../../design/components';
import PageHeading from '../../design/components/PageHeading';
import LeadChoiceChips from './LeadChoiceChips';

const LEAD_FILTER = {
	CURRENT: 'current',
	UNASSIGNED: 'unassigned',
	MY_LEADS: 'myLeads',
};

const FILTER_OPTIONS = [
	{ label: 'Current', value: LEAD_FILTER.CURRENT },
	{ label: 'Unassigned', value: LEAD_FILTER.UNASSIGNED },
	{ label: 'My leads', value: LEAD_FILTER.MY_LEADS },
];

const CURRENT_LEAD_STATUSES = [
	CLIENT_LEAD_STATUS.NEW_LEAD,
	CLIENT_LEAD_STATUS.CONTACTED,
	CLIENT_LEAD_STATUS.HOT_LEAD,
	CLIENT_LEAD_STATUS.WARM_LEAD,
	CLIENT_LEAD_STATUS.COLD_LEAD,
	CLIENT_LEAD_STATUS.ON_PAUSE,
];

const LeadListItem = ({ item, customProps }) => {
	const styles = useThemedStyles(createStyles);
	const assigneeName = item.assignee ? getUserName(item.assignee) : 'Unassigned';
	const followUpLabel = item.nextFollowUpAt
		? `Follow up ${moment(item.nextFollowUpAt).format('DD MMM')}`
		: 'No follow-up date';
	const statusLabel = CLIENT_LEAD_STATUS_NAMES[item.status] || 'Unknown status';

	return (
		<View style={styles.leadItem}>
			<ListRow
				title={item.name || 'Unnamed lead'}
				subtitle={`${assigneeName} · ${followUpLabel}`}
				// ListRow renders `right` instead of `value`; value still puts the status in the row's accessible label.
				value={statusLabel}
				right={(
					<View style={styles.leadRight}>
						<Tag label={statusLabel} style={styles.statusTag} />
						<Feather name="chevron-right" size={22} color={styles.chevron.color} />
					</View>
				)}
				onPress={() => customProps.openLead(item.id)}
			/>
		</View>
	);
};

const LeadList = ({ items, refetch, customProps }) => {
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);

	useRefetchOnFocus(refetch);

	return (
		<List
			items={items}
			listItemComponent={LeadListItem}
			onRefresh={refreshPage}
			isRefreshing={isRefreshing}
			emptyListMessage="No leads"
			autoScrollTop={true}
			removeTopMargin={true}
			showControls={true}
			customProps={customProps}
		/>
	);
};

const LeadListContainer = ListContainer(LeadList);

const LeadListView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { user, canViewClientsLeads, canManageClientsLeads } = useUserContext();
	const [activeFilter, setActiveFilter] = useState(LEAD_FILTER.CURRENT);

	const initialFilter = useMemo(() => ({
		statuses: CURRENT_LEAD_STATUSES,
		type: CLIENT_LEAD_TYPE.SALES_LEAD,
		...(activeFilter === LEAD_FILTER.UNASSIGNED ? { unassigned: true } : {}),
		...(activeFilter === LEAD_FILTER.MY_LEADS && user?.id ? { assignee: user.id } : {}),
	}), [activeFilter, user?.id]);

	const openLead = useCallback((id) => {
		navigation.navigate('LeadDetailView', { id });
	}, [navigation]);

	const customProps = useMemo(() => ({ openLead }), [openLead]);

	const openCreator = useCallback(() => {
		navigation.navigate('LeadCreatorView');
	}, [navigation]);

	if (!canViewClientsLeads) {
		return (
			<Screen>
				<EmptyState icon="lock" title="No access" message="You do not have access to leads." />
			</Screen>
		);
	}

	return (
		<Screen scroll={false}>
			<View style={styles.header}>
				<PageHeading
					title="Leads"
					subtitle="Review and progress sales leads from your phone"
					action={canManageClientsLeads && (
						<SecondaryButton
							label="New cold call"
							icon="plus"
							variant="tonal"
							onPress={openCreator}
						/>
					)}
				/>
				<LeadChoiceChips
					options={FILTER_OPTIONS}
					selectedValue={activeFilter}
					onSelect={setActiveFilter}
				/>
			</View>
			<LeadListContainer
				query={GET_MOBILE_LEADS}
				initialSortBy="created"
				initialSortDirection={-1}
				initialPerPage={30}
				initialFilter={initialFilter}
				listId="mobile_lead_list"
				customProps={customProps}
			/>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space } }) => ({
	header: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		gap: space.md,
	},
	leadItem: {
		paddingHorizontal: space.gutter,
		paddingVertical: space.xs,
	},
	leadRight: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
		maxWidth: '45%',
	},
	statusTag: {
		flexShrink: 1,
	},
	chevron: {
		color: colors.iconMuted,
	},
});

export default LeadListView;
